import './ManageMerchant.scss';

import { getMerchant, getMerchantStatus } from 'APIs/merchants/merchants';
import { AnyMerchant, MerchantStatusResponse } from 'APIs/merchants/merchants.types';
import Message from 'Components/Message';
import Spinner from 'Components/Spinner/Spinner';
import TabContent from 'Components/Tabs/TabContent';
import Tabs from 'Components/Tabs/Tabs';
import { baseUri } from 'Constants/Constants';
import { MouseEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import getErrorMessage from 'utils/getErrorMessage';

import MerchantBasicInfo from './Components/MerchantBasicInfo';
import MerchantDetails from './Components/MerchantDetails';
import MerchantInvoices from './Components/MerchantInvoices';
import MerchantOnboarding from './Components/MerchantOnboarding';
import MerchantPayouts from './Components/MerchantPayouts';
import MerchantSettings from './Components/MerchantSettings';
import MerchantTransactions from './Components/MerchantTransactions/.';
import MerchantUsers from './Components/MerchantUsers';
import { CreateMerchantContext } from './context';
import { LpBox, NavBackLink, SpinnerWrapper, Wrapper } from './styles';

const ManageMerchant = (): ReactElement => {
  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const marketplaceId = queryParams.get('marketplaceId');

  const { merchantId, manage } = useParams<{ merchantId: string; manage: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [merchant, setMerchant] = useState<AnyMerchant | null>(null);
  const [merchantStatus, setMerchantStatus] = useState<MerchantStatusResponse | null>(null);

  const handleCloseMsg = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setErrorMsg('');
  };

  const fetchMerchantDetails = useCallback(async () => {
    if (!apiAprilBaseUri || !merchantId) {
      return;
    }
    try {
      if (!merchant) setIsLoading(true);
      const merchantResp = await getMerchant(apiAprilBaseUri, merchantId);
      const statusResp = await getMerchantStatus(apiBaseUri, merchantId);
      setMerchant(merchantResp);
      setMerchantStatus(statusResp);
    } catch (e) {
      setErrorMsg(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [apiAprilBaseUri, apiBaseUri, merchant, merchantId]);

  useEffect(() => {
    if (!merchant) fetchMerchantDetails();
  }, [apiAprilBaseUri, apiBaseUri, fetchMerchantDetails, merchant, merchantId]);

  const contextValues = useMemo(
    () => ({
      merchantId,
      merchant,
      merchantStatus,
      setMerchant,
      setMerchantStatus,
      fetchMerchant: fetchMerchantDetails,
    }),
    [fetchMerchantDetails, merchant, merchantId, merchantStatus],
  );

  return (
    <Wrapper>
      <CreateMerchantContext.Provider value={contextValues}>
        <NavBackLink>
          {marketplaceId ? (
            <NavLink className="link-btn nav-link" to={`/admin-dashboard/marketplaces/${marketplaceId}/merchants`}>
              &lt; Back to marketplace
            </NavLink>
          ) : (
            <NavLink className="link-btn nav-link" to={`/admin-dashboard/merchants`}>
              &lt; Back to merchants
            </NavLink>
          )}
        </NavBackLink>
        {isLoading && (
          <SpinnerWrapper>
            <Spinner color="#0016D1" />
          </SpinnerWrapper>
        )}
        {!isLoading && errorMsg.length > 0 && (
          <Message className="mt-2 mb-0" description={errorMsg} success={false} handleClose={handleCloseMsg} />
        )}
        {!isLoading && (
          <>
            {merchant && merchantStatus && (
              <>
                <MerchantBasicInfo />
                <LpBox>
                  <Tabs
                    tabOptions={[
                      {
                        text: 'Onboard',
                        value: 'onboard',
                        uri: `${baseUri}/merchants/${merchantId}/onboard`,
                        isActive: manage === 'onboard',
                      },
                      {
                        text: 'Details',
                        value: 'details',
                        uri: `${baseUri}/merchants/${merchantId}/details`,
                        isActive: manage === 'details',
                      },
                      {
                        text: 'Settings',
                        value: 'settings',
                        uri: `${baseUri}/merchants/${merchantId}/settings`,
                        isActive: manage === 'settings',
                      },
                      {
                        text: 'Transactions',
                        value: 'transactions',
                        uri: `${baseUri}/merchants/${merchantId}/transactions`,
                        isActive: manage === 'transactions',
                      },
                      {
                        text: 'Payouts',
                        value: 'payouts',
                        uri: `${baseUri}/merchants/${merchantId}/payouts`,
                        isActive: manage === 'payouts',
                      },
                      {
                        text: 'Invoices',
                        value: 'invoices',
                        uri: `${baseUri}/merchants/${merchantId}/invoices`,
                        isActive: manage === 'invoices',
                      },
                      {
                        text: 'Users',
                        value: 'users',
                        uri: `${baseUri}/merchants/${merchantId}/users`,
                        isActive: manage === 'users',
                      },
                    ]}
                    testId={'merchant-menu'}
                  >
                    <TabContent>{manage === 'onboard' && <MerchantOnboarding />}</TabContent>
                    <TabContent>{manage === 'details' && <MerchantDetails />}</TabContent>
                    <TabContent>{manage === 'settings' && <MerchantSettings />}</TabContent>
                    <TabContent>{manage === 'transactions' && <MerchantTransactions />}</TabContent>
                    <TabContent>{manage === 'payouts' && merchant && <MerchantPayouts />}</TabContent>
                    <TabContent>{manage === 'invoices' && merchant && <MerchantInvoices />}</TabContent>
                    <TabContent>
                      <TabContent>{manage === 'users' && merchant && <MerchantUsers />}</TabContent>
                    </TabContent>
                  </Tabs>
                </LpBox>
              </>
            )}
          </>
        )}
      </CreateMerchantContext.Provider>
    </Wrapper>
  );
};

export default ManageMerchant;
